import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  Paper
} from '@mui/material';
import { Sale } from '../../utils/types';
import { currencyFormat } from '../../utils/constants';

interface InvoicePrintTemplateProps {
  sale: Sale;
  companyInfo: {
    name: string;
    address: string;
    phone: string;
    email: string;
    logo?: string;
    vatNumber?: string;
  };
}

const InvoicePrintTemplate: React.FC<InvoicePrintTemplateProps> = ({ sale, companyInfo }) => {
  return (
    <Box sx={{ p: 4, maxWidth: '800px', margin: '0 auto', fontFamily: 'Cairo, sans-serif' }}>
      {/* رأس الفاتورة */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h5" gutterBottom>{companyInfo.name}</Typography>
          <Typography variant="body2">{companyInfo.address}</Typography>
          <Typography variant="body2">هاتف: {companyInfo.phone}</Typography>
          <Typography variant="body2">البريد الإلكتروني: {companyInfo.email}</Typography>
          {companyInfo.vatNumber && (
            <Typography variant="body2">الرقم الضريبي: {companyInfo.vatNumber}</Typography>
          )}
        </Box>
        <Box sx={{ textAlign: 'left' }}>
          {companyInfo.logo && (
            <img src={companyInfo.logo} alt="Company Logo" style={{ maxHeight: '80px' }} />
          )}
          <Typography variant="h6">فاتورة مبيعات</Typography>
          <Typography variant="body2">رقم الفاتورة: {sale.id.substring(0, 8)}</Typography>
          <Typography variant="body2">
            التاريخ: {new Date(sale.createdAt).toLocaleDateString('ar-EG')}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* معلومات العميل */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>معلومات العميل</Typography>
        <Typography variant="body1">الاسم: {sale.customerName || 'عميل عام'}</Typography>
        <Typography variant="body1">الفرع: {sale.branchName}</Typography>
      </Box>

      {/* تفاصيل الفاتورة */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>المنتج</TableCell>
              <TableCell align="center">الكمية</TableCell>
              <TableCell align="center">السعر</TableCell>
              <TableCell align="center">الإجمالي</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sale.items.map((item, index) => (
              <TableRow key={item.itemId}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell align="center">{item.quantity}</TableCell>
                <TableCell align="center">{currencyFormat(item.price)}</TableCell>
                <TableCell align="center">{currencyFormat(item.total)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* ملخص الفاتورة */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Box sx={{ width: '300px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body1">المجموع:</Typography>
            <Typography variant="body1">{currencyFormat(sale.total)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body1">الخصم:</Typography>
            <Typography variant="body1">{currencyFormat(sale.discount)}</Typography>
          </Box>
          {sale.tax !== undefined && sale.tax > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1">الضريبة:</Typography>
              <Typography variant="body1">{currencyFormat(sale.tax)}</Typography>
            </Box>
          )}
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6">الإجمالي:</Typography>
            <Typography variant="h6">{currencyFormat(sale.grandTotal)}</Typography>
          </Box>
        </Box>
      </Box>

      {/* معلومات الدفع */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body1">
          طريقة الدفع: {
            sale.paymentMethod === 'cash' ? 'نقدًا' :
            sale.paymentMethod === 'card' ? 'بطاقة ائتمان' :
            sale.paymentMethod === 'bankTransfer' ? 'تحويل بنكي' :
            sale.paymentMethod === 'stc' ? 'STC Pay' :
            sale.paymentMethod === 'mada' ? 'مدى' :
            sale.paymentMethod
          }
        </Typography>
        <Typography variant="body1">
          حالة الدفع: {
            sale.paymentStatus === 'paid' ? 'مدفوع' :
            sale.paymentStatus === 'partiallyPaid' ? 'مدفوع جزئيًا' :
            sale.paymentStatus === 'unpaid' ? 'غير مدفوع' :
            sale.paymentStatus
          }
        </Typography>
        {sale.paymentStatus === 'partiallyPaid' && (
          <>
            <Typography variant="body1">المبلغ المدفوع: {currencyFormat(sale.amountPaid || 0)}</Typography>
            <Typography variant="body1">المبلغ المتبقي: {currencyFormat(sale.remainingAmount || 0)}</Typography>
          </>
        )}
      </Box>

      {/* ملاحظات */}
      {sale.notes && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" gutterBottom>ملاحظات:</Typography>
          <Typography variant="body2">{sale.notes}</Typography>
        </Box>
      )}

      {/* تذييل الفاتورة */}
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2">شكرًا لتعاملكم معنا</Typography>
        <Typography variant="body2">{companyInfo.name} - {companyInfo.phone}</Typography>
      </Box>
    </Box>
  );
};

export default InvoicePrintTemplate;
